import JSZip from 'jszip';
import { Scenario } from '../../types';

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|svg)$/i;
const VIDEO_EXT = /\.(mp4|webm|ogg|mov)$/i;
const MAX_UNCOMPRESSED_BYTES = 1024 * 1024 * 1024;
const MAX_FILE_COUNT = 2000;

export interface ParsedScenarioAsset {
  path: string;
  blob: Blob;
}

export interface ParsedScenarioZip {
  suggestedId: string;
  scenario: Scenario;
  assets: ParsedScenarioAsset[];
}

export interface ScenarioAssetFileMeta {
  path: string;
  size: number;
}

/** Assets from a zip that are new or whose size differs from the draft on the server. */
export function assetsToUpload(
  zipAssets: ParsedScenarioAsset[],
  existing: ScenarioAssetFileMeta[]
): ParsedScenarioAsset[] {
  const sizeByPath = new Map(
    existing.map((item) => [item.path.replace(/\\/g, '/'), item.size])
  );

  return zipAssets.filter((asset) => {
    const normalizedPath = asset.path.replace(/\\/g, '/');
    const existingSize = sizeByPath.get(normalizedPath);
    if (existingSize === undefined) return true;
    return existingSize !== asset.blob.size;
  });
}

function isMacOsMetadata(zipPath: string): boolean {
  const normalized = zipPath.replace(/\\/g, '/');
  return normalized.includes('__MACOSX') || normalized.split('/').pop()?.startsWith('._') === true;
}

function isIgnoredEntry(zipPath: string): boolean {
  const normalized = zipPath.replace(/\\/g, '/');
  const base = normalized.split('/').pop() ?? '';
  return isMacOsMetadata(normalized) || base === '.DS_Store' || base === '.keep';
}

function assertSafeRelativePath(relativePath: string): void {
  const normalized = relativePath.replace(/\\/g, '/');
  if (!normalized || normalized.startsWith('/') || normalized.includes('..')) {
    throw new Error(`Chemin invalide dans le zip : ${relativePath}`);
  }
}

function isAllowedAssetPath(relativePath: string): boolean {
  const normalized = relativePath.replace(/\\/g, '/');
  if (!normalized.startsWith('assets/')) return false;
  const filename = normalized.split('/').pop() ?? '';
  return IMAGE_EXT.test(filename) || VIDEO_EXT.test(filename);
}

/**
 * Strip a single root folder when every entry shares it (e.g. foo/scenario.json).
 */
function stripCommonRootPrefix(paths: string[]): { prefix: string; paths: string[] } {
  if (paths.length === 0) return { prefix: '', paths };

  const splitPaths = paths.map((p) => p.replace(/\\/g, '/').split('/').filter(Boolean));
  const firstSegments = splitPaths.map((parts) => parts[0]).filter(Boolean);
  if (firstSegments.length === 0) return { prefix: '', paths };

  const root = firstSegments[0];
  const allShareRoot = splitPaths.every((parts) => parts[0] === root);
  if (!allShareRoot || splitPaths.every((parts) => parts.length === 1)) {
    return { prefix: '', paths };
  }

  const stripped = paths.map((p) => {
    const parts = p.replace(/\\/g, '/').split('/').filter(Boolean);
    return parts.slice(1).join('/');
  });
  return { prefix: root, paths: stripped };
}

/**
 * Strip draft/ or master/ layer prefix when all paths share it.
 */
function stripLayerPrefix(paths: string[]): string[] {
  if (paths.length === 0) return paths;
  const normalized = paths.map((p) => p.replace(/\\/g, '/'));
  const layers = new Set(normalized.map((p) => p.split('/')[0]).filter(Boolean));
  if (layers.size !== 1) return paths;
  const layer = [...layers][0];
  if (layer !== 'draft' && layer !== 'master') return paths;
  return normalized.map((p) => p.split('/').slice(1).join('/')).filter(Boolean);
}

function suggestIdFromZip(file: File, rootPrefix: string): string {
  if (rootPrefix) return rootPrefix;
  const base = file.name.replace(/\.zip$/i, '').trim();
  return base || 'nouveau-scenario';
}

function validateScenarioJson(scenario: unknown): Scenario {
  const s = scenario as Scenario;
  if (!s?.metadata || !Array.isArray(s.scenes)) {
    throw new Error('scenario.json invalide : metadata et scenes sont requis.');
  }
  if (!s.metadata.startScene) {
    throw new Error('scenario.json invalide : metadata.startScene est requis.');
  }
  return s;
}

/**
 * Parse a scenario ZIP exported from Scenario Studio (or matching folder layout).
 */
export async function parseScenarioZip(file: File): Promise<ParsedScenarioZip> {
  const zip = await JSZip.loadAsync(file);
  const rawEntries: Array<{ zipPath: string; entry: JSZip.JSZipObject }> = [];

  for (const [zipPath, entry] of Object.entries(zip.files)) {
    if (entry.dir || isIgnoredEntry(zipPath)) continue;
    rawEntries.push({ zipPath, entry });
  }

  if (rawEntries.length === 0) {
    throw new Error('Le fichier zip est vide ou ne contient aucun fichier utilisable.');
  }
  if (rawEntries.length > MAX_FILE_COUNT) {
    throw new Error(`Trop de fichiers dans le zip (max ${MAX_FILE_COUNT}).`);
  }

  let paths = rawEntries.map((e) => e.zipPath.replace(/\\/g, '/'));
  const { prefix: rootPrefix, paths: strippedPaths } = stripCommonRootPrefix(paths);
  paths = stripLayerPrefix(strippedPaths);

  const pathByZipPath = new Map<string, string>();
  rawEntries.forEach((item, index) => {
    pathByZipPath.set(item.zipPath, paths[index] ?? '');
  });

  const scenarioJsonPath = paths.find((p) => p === 'scenario.json');
  if (!scenarioJsonPath) {
    throw new Error('scenario.json introuvable à la racine du zip.');
  }

  const scenarioEntry = rawEntries.find((e) => pathByZipPath.get(e.zipPath) === 'scenario.json');
  if (!scenarioEntry) {
    throw new Error('scenario.json introuvable.');
  }

  let totalBytes = 0;
  const assets: ParsedScenarioAsset[] = [];

  for (const item of rawEntries) {
    const relativePath = pathByZipPath.get(item.zipPath) ?? '';
    if (!relativePath || relativePath === 'scenario.json') continue;

    assertSafeRelativePath(relativePath);
    if (!isAllowedAssetPath(relativePath)) continue;

    const blob = await item.entry.async('blob');
    totalBytes += blob.size;
    if (totalBytes > MAX_UNCOMPRESSED_BYTES) {
      throw new Error(`Le contenu décompressé dépasse 1 Go.`);
    }
    assets.push({ path: relativePath, blob });
  }

  const scenarioText = await scenarioEntry.entry.async('text');
  let scenario: Scenario;
  try {
    scenario = validateScenarioJson(JSON.parse(scenarioText));
  } catch (err) {
    if (err instanceof SyntaxError) {
      throw new Error('scenario.json n’est pas un JSON valide.');
    }
    throw err;
  }

  return {
    suggestedId: suggestIdFromZip(file, rootPrefix),
    scenario,
    assets,
  };
}
